/**
 * OneVet Pet Quiz
 * State machine + recommendation engine + checkout flow
 * Backup: backups/2026-05-11/theme-034330/
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-pet-quiz]');
  if (!root) return;

  const catalogEl = document.getElementById('petQuizCatalog');
  const CATALOG = catalogEl ? JSON.parse(catalogEl.textContent) : {};

  const STEPS_CAT = ['petType', 'age', 'concerns', 'lifestyle', 'email'];
  const STEPS_DOG = ['petType', 'age', 'size', 'concerns', 'lifestyle', 'email'];

  const state = {
    current: 'intro',
    stepIndex: 0,
    flow: STEPS_CAT,
    answers: {
      petType: null,
      age: null,
      size: null,
      concerns: [],
      lifestyle: null,
      name: null,
      email: null,
      petName: null,
    },
  };

  /* ---------------- Recommendation Engine ---------------- */

  // Concern → primary product key per pet type
  const CONCERN_MAP = {
    joint:     { cat: 'glukozamin',  dog: 'glukozamin' },
    fur:       { cat: 'biotin',      dog: 'biotin' },
    digestion: { cat: 'prob_kedi',   dog: 'prob_kopek' },
    immunity:  { cat: 'omega',       dog: 'omega' },
    energy:    { cat: 'omega',       dog: 'omega' },
    dental:    { cat: 'dis',         dog: 'dis' },
    growth:    { cat: 'amino',       dog: 'sut' },
    kidney:    { cat: 'fosfat',      dog: 'fosfat' },
    hairball:  { cat: 'malt',        dog: null },
    stress:    { cat: 'relax',       dog: null },
    general:   { cat: 'multipaste',  dog: 'collagen' },
  };

  // Secondary suggestion per concern (eğer 2. concern yoksa)
  const SECONDARY_MAP = {
    joint:     { cat: 'collagen',   dog: 'collagen' },
    fur:       { cat: 'malt',       dog: 'omega' },
    digestion: { cat: 'prebiotic',  dog: 'prebiotic' },
    immunity:  { cat: 'multipaste', dog: 'omega' },
    energy:    { cat: 'amino',      dog: 'amino' },
    dental:    { cat: 'omega',      dog: 'omega' },
    growth:    { cat: 'amino',      dog: 'amino' },
    kidney:    { cat: 'omega',      dog: 'omega' },
    hairball:  { cat: 'biotin',     dog: null },
    stress:    { cat: 'omega',      dog: null },
    general:   { cat: 'omega',      dog: 'omega' },
  };

  // Yaşa göre BASE ürün — her zaman önerilir
  function getBaseProduct(petType, age) {
    if (age === 'puppy') {
      return petType === 'dog' ? 'sut' : 'amino';
    }
    if (age === 'senior') {
      return 'collagen';
    }
    return 'multi';
  }

  function isAvailable(key) {
    const p = CATALOG[key];
    return !!(p && p.available && p.variant_id);
  }

  function getRecommendations() {
    const { petType, age, concerns } = state.answers;
    const recs = [];
    const used = new Set();

    // Dinamik max ürün: 1 base + concerns sayısı, max 4
    const maxProducts = Math.min(4, 1 + Math.max(1, concerns.length));

    // 1) Base
    const baseKey = getBaseProduct(petType, age);
    if (baseKey && isAvailable(baseKey)) {
      recs.push({ key: baseKey, role: 'Temel Destek', reason: roleReason('base', state.answers) });
      used.add(baseKey);
    } else if (isAvailable('multi')) {
      recs.push({ key: 'multi', role: 'Temel Destek', reason: 'Günlük temel vitamin desteği — her pet için baz program.' });
      used.add('multi');
    }

    // 2) Her concern için birincil ürün
    for (const c of concerns) {
      if (recs.length >= maxProducts) break;
      const map = CONCERN_MAP[c];
      if (!map) continue;
      const key = map[petType];
      if (key && isAvailable(key) && !used.has(key)) {
        recs.push({ key, role: roleLabel(c), reason: concernReason(c, state.answers) });
        used.add(key);
      } else if (!isAvailable(key)) {
        const sec = SECONDARY_MAP[c]?.[petType];
        if (sec && isAvailable(sec) && !used.has(sec)) {
          recs.push({ key: sec, role: roleLabel(c), reason: concernReason(c, state.answers) });
          used.add(sec);
        }
      }
    }

    // 3) Min 3 ürün hedefi - eksik kalanı secondary/fallback ile doldur
    const minTarget = Math.min(3, maxProducts);
    while (recs.length < minTarget) {
      let added = false;
      for (const c of concerns) {
        const sec = SECONDARY_MAP[c]?.[petType];
        if (sec && isAvailable(sec) && !used.has(sec)) {
          recs.push({ key: sec, role: roleLabel(c) + ' (Ek)', reason: 'Birincil ürünle birlikte kullanıldığında daha hızlı sonuç verir.' });
          used.add(sec);
          added = true;
          break;
        }
      }
      if (!added) {
        const fallbacks = ['omega', 'multipaste', 'multi', 'biotin', 'collagen', 'glukozamin', 'prebiotic'];
        const fb = fallbacks.find((k) => !used.has(k) && isAvailable(k));
        if (!fb) break;
        recs.push({ key: fb, role: 'Genel Sağlık', reason: 'Petinin uzun vadeli sağlığı için temel takviye.' });
        used.add(fb);
      }
      if (recs.length >= minTarget) break;
    }

    return recs.slice(0, maxProducts);
  }

  // Generate fake Rx number for prescription feel
  function generateRxNo() {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `${d.getFullYear().toString().slice(2)}${month}${day}-${rand}`;
  }

  function roleLabel(concern) {
    const labels = {
      joint: 'Eklem & Hareket',
      fur: 'Tüy & Cilt',
      digestion: 'Sindirim',
      immunity: 'Bağışıklık',
      energy: 'Beyin & Enerji',
      dental: 'Diş & Ağız',
      growth: 'Büyüme & Gelişim',
      kidney: 'Böbrek Sağlığı',
      hairball: 'Tüy Yumağı',
      stress: 'Stres Azaltıcı',
      general: 'Genel Sağlık',
    };
    return labels[concern] || 'Destek';
  }

  function roleReason(role, ans) {
    if (role === 'base') {
      if (ans.age === 'puppy') return 'Yavru döneminde gelişim için temel takviye.';
      if (ans.age === 'senior') return 'Yaşlı pet için kas ve eklem gücünü korur.';
      return 'Günlük temel vitamin desteği — her pet için baz program.';
    }
    return '';
  }

  function concernReason(concern, ans) {
    const reasons = {
      joint: 'Eklem ağrısını azaltır, atlama-zıplama hareketlerini destekler.',
      fur: 'Tüy döküntüsünü azaltır, parlak ve sağlıklı kürk için.',
      digestion: 'Bağırsak florasını dengeler, ishal-kabızlık dönemlerinde yardımcı olur.',
      immunity: 'Bağışıklık sistemini güçlendirir, hastalık dönemlerini kısaltır.',
      energy: 'Yaşlı petlerde uyuşukluğu azaltır, beyin sağlığını destekler.',
      dental: 'Diş taşı oluşumunu yavaşlatır, ağız kokusunu giderir.',
      growth: 'Yavru döneminde sağlıklı büyüme için elzem aminoasit ve protein desteği.',
      kidney: 'Yaşlı petlerde böbrek fonksiyonlarını destekler, fosfat dengesi.',
      hairball: 'Tüy yumağı oluşumunu önler, sindirim sistemini rahatlatır.',
      stress: 'Stres ve anksiyete dönemlerinde sakinleştirici etki sağlar.',
      general: 'Petinin genel sağlığını korumak için bütüncül takviye.',
    };
    return reasons[concern] || '';
  }

  /* ---------------- Bundle Discount Calc ---------------- */

  function calcBundle(recs) {
    const total = recs.reduce((sum, r) => sum + (CATALOG[r.key]?.price_raw || 0), 0);
    let discountPct = 0;
    if (recs.length === 1) discountPct = 10;
    else if (recs.length === 2) discountPct = 15;
    else if (recs.length === 3) discountPct = 20;
    else if (recs.length >= 4) discountPct = 25;

    const discount = total * (discountPct / 100);
    const final = total - discount;
    return {
      total: total,
      discountPct: discountPct,
      discount: discount,
      final: final,
      savings: discount,
    };
  }

  function fmtTL(n) {
    return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
  }

  /* ---------------- Navigation ---------------- */

  function showScreen(name) {
    root.querySelectorAll('[data-pq-screen]').forEach((el) => {
      el.hidden = el.dataset.pqScreen !== name;
    });
    const flow = root.querySelector('[data-pq-flow]');
    if (flow) flow.hidden = (name !== 'flow');
    state.current = name;
  }

  function showStep(stepName) {
    root.querySelectorAll('[data-pq-step]').forEach((el) => {
      el.hidden = el.dataset.pqStep !== stepName;
    });
    state.stepIndex = state.flow.indexOf(stepName);
    updateProgress();
    updateBackBtn();
    // Scroll up
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function updateProgress() {
    const total = state.flow.length;
    const idx = Math.max(0, state.stepIndex);
    const pct = Math.round(((idx + 1) / total) * 100);
    const bar = root.querySelector('[data-pq-progress]');
    const stepEl = root.querySelector('[data-pq-progress-step]');
    const totalEl = root.querySelector('[data-pq-progress-total]');
    if (bar) bar.style.width = pct + '%';
    if (stepEl) stepEl.textContent = String(idx + 1);
    if (totalEl) totalEl.textContent = String(total);
  }

  function updateBackBtn() {
    const back = root.querySelector('[data-pq-back]');
    if (back) back.hidden = (state.stepIndex <= 0);
  }

  function nextStep() {
    const nextIdx = state.stepIndex + 1;
    if (nextIdx >= state.flow.length) {
      // Quiz done? Email step is last; result shown after email submit
      return;
    }
    const next = state.flow[nextIdx];
    showStep(next);
  }

  function prevStep() {
    const prevIdx = state.stepIndex - 1;
    if (prevIdx < 0) return;
    const prev = state.flow[prevIdx];
    showStep(prev);
  }

  /* ---------------- Concern multi-select ---------------- */

  function toggleConcern(value) {
    const arr = state.answers.concerns;
    const i = arr.indexOf(value);
    const max = parseInt(root.querySelector('[data-pq-multi-max]')?.dataset.pqMultiMax || '4', 10);
    if (i >= 0) {
      arr.splice(i, 1);
    } else if (arr.length >= max) {
      shakeConcernHint();
      return;
    } else {
      arr.push(value);
    }
    refreshConcernUI();
  }

  function shakeConcernHint() {
    const hint = root.querySelector('[data-pq-concerns-hint]');
    if (!hint) return;
    hint.classList.remove('pq-shake');
    void hint.offsetWidth;
    hint.classList.add('pq-shake');
  }

  function refreshConcernUI() {
    const max = parseInt(root.querySelector('[data-pq-multi-max]')?.dataset.pqMultiMax || '4', 10);
    const atMax = state.answers.concerns.length >= max;
    root.querySelectorAll('[data-pq-multi="concerns"]').forEach((btn) => {
      const selected = state.answers.concerns.includes(btn.dataset.pqValue);
      btn.classList.toggle('is-selected', selected);
      btn.classList.toggle('is-disabled', atMax && !selected);
    });
    const hint = root.querySelector('[data-pq-concerns-hint]');
    if (hint) {
      const count = state.answers.concerns.length;
      if (count === 0) {
        hint.textContent = 'İhtiyaç gördüğün tüm alanları seç — en fazla 4 alan';
      } else if (count < max) {
        hint.textContent = `${count} alan seçildi · ${max - count} daha seçebilirsin (veya devam et)`;
      } else {
        hint.textContent = `${max}/${max} seçildi · Devam edebilir veya seçimini değiştirebilirsin`;
      }
    }
    const nextBtn = root.querySelector('[data-pq-next="concerns"]');
    if (nextBtn) nextBtn.disabled = state.answers.concerns.length === 0;
  }

  /* ---------------- Filter cat-only concerns for dogs ---------------- */

  function filterConcernsForPetType() {
    const isCat = state.answers.petType === 'cat';
    root.querySelectorAll('.pq-option--cat-only').forEach((el) => {
      el.style.display = isCat ? '' : 'none';
    });
  }

  /* ---------------- Pet name fill ---------------- */

  function refreshPetName() {
    const name = state.answers.petName || (state.answers.petType === 'cat' ? 'Kedin' : state.answers.petType === 'dog' ? 'Köpeğin' : 'Petin');
    root.querySelectorAll('[data-pq-petname]').forEach((el) => { el.textContent = name; });
    root.querySelectorAll('[data-pq-result-petname]').forEach((el) => { el.textContent = name; });
    root.querySelectorAll('[data-pq-result-petname2]').forEach((el) => { el.textContent = name; });
  }

  /* ---------------- Email submission ---------------- */

  async function submitEmail(form) {
    const fd = new FormData(form);
    state.answers.name = fd.get('name')?.trim() || null;
    state.answers.email = fd.get('email')?.trim() || null;
    state.answers.petName = fd.get('petName')?.trim() || null;

    if (!state.answers.email || !state.answers.name) return false;

    // Build customer tags
    const tags = ['quiz_taker', `${state.answers.petType}_owner`];
    if (state.answers.age) tags.push(`pet_${state.answers.age}`);
    if (state.answers.size) tags.push(`size_${state.answers.size}`);
    if (state.answers.lifestyle) tags.push(`lifestyle_${state.answers.lifestyle}`);
    state.answers.concerns.forEach((c) => tags.push(`concern_${c}`));

    const labels = {
      cat: 'Kedi', dog: 'Köpek',
      puppy: 'Yavru (0-12 ay)', adult: 'Yetişkin (1-7 yaş)', senior: 'Yaşlı (7+ yaş)', unknown: 'Bilinmiyor',
      small: 'Küçük ırk (0-10kg)', medium: 'Orta ırk (10-25kg)', large: 'Büyük ırk (25-45kg)', xl: 'Dev ırk (45+kg)',
      indoor: 'Apartman / içeride', mixed: 'Bahçeli / karma', outdoor: 'Sürekli aktif',
      joint: 'Eklem & Hareket', fur: 'Tüy & Cilt', digestion: 'Sindirim', immunity: 'Bağışıklık',
      energy: 'Beyin & Enerji', dental: 'Diş & Ağız', growth: 'Büyüme', kidney: 'Böbrek & Yaşlanma',
      hairball: 'Tüy Yumağı', stress: 'Stres & Anksiyete', general: 'Genel Sağlık',
    };
    const ans = state.answers;
    const concernsList = ans.concerns.map(c => labels[c] || c).join(', ');

    // 1️⃣  Customer DB'ye kayıt (tag'lerle, Klaviyo segmentasyonu için)
    const customerPayload = new FormData();
    customerPayload.append('form_type', 'customer');
    customerPayload.append('utf8', '✓');
    customerPayload.append('contact[tags]', tags.join(','));
    customerPayload.append('contact[email]', ans.email);
    customerPayload.append('contact[first_name]', ans.name);
    customerPayload.append('contact[accepts_marketing]', 'true');
    customerPayload.append('contact[note]', `Pet Quiz: ${labels[ans.petType]||ans.petType} | ${labels[ans.age]||ans.age||'?'} | Endişeler: ${concernsList || '-'} | Pet adı: ${ans.petName || '-'}`);

    try {
      await fetch('/contact#contact_form', {
        method: 'POST',
        body: customerPayload,
        credentials: 'same-origin',
      });
    } catch (e) { console.warn('[PetQuiz] customer signup failed', e); }

    // 2️⃣  Admin'e (mağaza sahibine) detaylı bildirim email'i gönder
    const adminPayload = new FormData();
    adminPayload.append('form_type', 'contact');
    adminPayload.append('utf8', '✓');
    adminPayload.append('contact[name]', `[PET QUIZ] ${ans.name}`);
    adminPayload.append('contact[email]', ans.email);
    adminPayload.append('contact[subject]', `Yeni Pet Quiz: ${ans.name} (${labels[ans.petType]||ans.petType})`);
    adminPayload.append('contact[body]',
      `Yeni Pet Quiz tamamlandı!\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━\n` +
      `KULLANICI:\n` +
      `  Ad: ${ans.name}\n` +
      `  Email: ${ans.email}\n` +
      `  Pet adı: ${ans.petName || '(belirtilmedi)'}\n\n` +
      `PET PROFİLİ:\n` +
      `  Tür: ${labels[ans.petType] || ans.petType}\n` +
      `  Yaş: ${labels[ans.age] || ans.age || '?'}\n` +
      (ans.size ? `  Boyut: ${labels[ans.size]}\n` : '') +
      `  Yaşam tarzı: ${labels[ans.lifestyle] || ans.lifestyle || '?'}\n\n` +
      `BELİRTİLEN ENDİŞELER:\n` +
      `  ${concernsList || '(yok)'}\n\n` +
      `ÖNERİLEN ÜRÜNLER:\n` +
      (state._recs ? state._recs.map((r,i)=>`  ${i+1}. ${CATALOG[r.key]?.title || r.key} — ${r.role}`).join('\n') : '  (henüz hesaplanmadı)') + '\n\n' +
      `Customer Tag'leri: ${tags.join(', ')}\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━\n` +
      `Bu kullanıcıyla 24 saat içinde iletişime geç — sıcak lead.\n`
    );

    try {
      await fetch('/contact#contact_form', {
        method: 'POST',
        body: adminPayload,
        credentials: 'same-origin',
      });
    } catch (e) { console.warn('[PetQuiz] admin notify failed', e); }

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'pet_quiz_completed',
        pet_type: ans.petType,
        pet_age: ans.age,
        concerns: ans.concerns,
        email: ans.email,
      });
    }

    // localStorage
    try {
      localStorage.setItem('onevet_quiz', JSON.stringify({ ...ans, ts: Date.now() }));
    } catch (_) {}

    return true;
  }

  /* ---------------- Render Result ---------------- */

  function renderResult() {
    refreshPetName();

    const recs = getRecommendations();
    const bundle = calcBundle(recs);

    // Meta line
    const metaParts = [];
    if (state.answers.age) metaParts.push(({ puppy: 'Yavru', adult: 'Yetişkin', senior: 'Yaşlı', unknown: '' })[state.answers.age]);
    if (state.answers.size) metaParts.push(({ small: 'Küçük', medium: 'Orta', large: 'Büyük', xl: 'Dev' })[state.answers.size] + ' ırk');
    if (state.answers.lifestyle) metaParts.push(({ indoor: 'İçeride', mixed: 'Karma', outdoor: 'Aktif' })[state.answers.lifestyle]);
    state.answers.concerns.forEach((c) => metaParts.push(roleLabel(c)));
    const metaEl = root.querySelector('[data-pq-result-meta]');
    if (metaEl) metaEl.textContent = metaParts.filter(Boolean).join(' · ');

    // Rx No
    const rxNo = state._rxNo || (state._rxNo = generateRxNo());
    const rxEl = root.querySelector('[data-pq-rx-no]');
    if (rxEl) rxEl.textContent = rxNo;

    // Cards
    const cardsEl = root.querySelector('[data-pq-result-cards]');
    cardsEl.innerHTML = '';
    recs.forEach((r, i) => {
      const p = CATALOG[r.key];
      if (!p) return;
      const card = document.createElement('div');
      card.className = 'pq-card';
      const seq = String(i + 1).padStart(2, '0');
      card.innerHTML = `
        <div class="pq-card__rx-no">№ ${seq}</div>
        <div class="pq-card__media">
          <img src="${p.image}" alt="${p.title}" loading="lazy">
        </div>
        <div class="pq-card__body">
          <div class="pq-card__role">${r.role}</div>
          <h3 class="pq-card__title"><a href="${p.url}" target="_blank" rel="noopener">${p.title}</a></h3>
          <p class="pq-card__reason">${r.reason}</p>
          <div class="pq-card__price">${fmtTL(p.price_raw)}</div>
        </div>
      `;
      cardsEl.appendChild(card);
    });

    // Bundle calc display
    root.querySelector('[data-pq-bundle-total]').textContent = fmtTL(bundle.total);
    root.querySelector('[data-pq-bundle-discount-label]').textContent = `· %${bundle.discountPct}`;
    root.querySelector('[data-pq-bundle-discount]').textContent = '−' + fmtTL(bundle.discount);
    root.querySelector('[data-pq-bundle-final]').textContent = fmtTL(bundle.final);
    root.querySelector('[data-pq-bundle-savings]').textContent = fmtTL(bundle.savings);

    // Save recs for cart action
    state._recs = recs;
    state._bundle = bundle;

    showScreen('result');
  }

  /* ---------------- Add bundle to cart ---------------- */

  async function addBundleToCart() {
    if (!state._recs?.length) return;

    // Stokta olanları filtrele (extra safety — getRecommendations zaten filtreliyor)
    const validRecs = state._recs.filter((r) => isAvailable(r.key));
    const items = validRecs.map((r) => ({ id: CATALOG[r.key].variant_id, quantity: 1 }));

    if (!items.length) {
      alert('Üzgünüz, önerilen ürünler şu anda stokta yok. Birkaç gün içinde stokta olacak — bizi takip et!');
      return;
    }

    const btn = root.querySelector('[data-pq-add-bundle]');
    const originalLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sepete ekleniyor...'; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data?.description || data?.message || 'Sepete eklenemedi. Lütfen tekrar dene.';
        throw new Error(msg);
      }

      // Discount code by recommendation count
      const codeMap = { 1: 'RECETE10', 2: 'RECETE15', 3: 'RECETE20', 4: 'RECETE25' };
      const code = codeMap[Math.min(items.length, 4)] || 'RECETE20';

      // Apply discount via discount URL
      window.location.href = `/discount/${code}?redirect=/cart`;
    } catch (e) {
      console.error('[PetQuiz] add bundle failed', e);
      if (btn) { btn.disabled = false; btn.textContent = originalLabel || '🛒 Paketi Sepete Ekle'; }
      alert(e.message || 'Sepete eklenemedi. Lütfen tekrar dene.');
    }
  }

  /* ---------------- Event Bindings ---------------- */

  // Start
  root.querySelector('[data-pq-start]')?.addEventListener('click', () => {
    showScreen('flow');
    showStep(state.flow[0]);
  });

  // Single-select answers (auto-advance)
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pq-answer]');
    if (!btn) return;
    const key = btn.dataset.pqAnswer;
    const value = btn.dataset.pqValue;
    state.answers[key] = value;

    // Side effects
    if (key === 'petType') {
      state.flow = value === 'dog' ? STEPS_DOG : STEPS_CAT;
      filterConcernsForPetType();
    }

    // Visual feedback
    btn.parentElement.querySelectorAll('[data-pq-answer]').forEach((b) => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    refreshPetName();
    setTimeout(() => nextStep(), 220);
  });

  // Concern multi-select
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pq-multi="concerns"]');
    if (!btn) return;
    toggleConcern(btn.dataset.pqValue);
  });

  // Concern next
  root.querySelector('[data-pq-next="concerns"]')?.addEventListener('click', () => {
    if (state.answers.concerns.length === 0) return;
    nextStep();
  });

  // Back
  root.querySelector('[data-pq-back]')?.addEventListener('click', prevStep);

  // Email form
  root.querySelector('[data-pq-email-form]')?.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Validate all fields
    const fd = new FormData(e.target);
    const nameErr = validateField('name', fd.get('name'));
    const emailErr = validateField('email', fd.get('email'));
    showFieldError('name', nameErr);
    showFieldError('email', emailErr);
    if (nameErr || emailErr) {
      const firstErr = root.querySelector('[data-pq-field].has-error input');
      if (firstErr) firstErr.focus();
      return;
    }

    const submitBtn = e.target.querySelector('button[type="submit"]');
    const lbl = submitBtn?.querySelector('.pq-btn__label');
    const spinner = submitBtn?.querySelector('.pq-btn__spinner');
    if (submitBtn) submitBtn.disabled = true;
    if (lbl) lbl.textContent = 'Reçeten hazırlanıyor...';
    if (spinner) spinner.hidden = false;

    const ok = await submitEmail(e.target);
    if (ok) {
      renderResult();
    } else {
      if (submitBtn) submitBtn.disabled = false;
      if (lbl) lbl.textContent = 'Reçetemi Göster';
      if (spinner) spinner.hidden = true;
    }
  });

  // Add bundle
  root.querySelector('[data-pq-add-bundle]')?.addEventListener('click', addBundleToCart);

  // Restart
  root.querySelector('[data-pq-restart]')?.addEventListener('click', () => {
    state.answers = { petType: null, age: null, size: null, concerns: [], lifestyle: null, name: null, email: null, petName: null };
    state.flow = STEPS_CAT;
    state.stepIndex = 0;
    state._recs = null;
    state._bundle = null;
    state._rxNo = null;
    try { localStorage.removeItem('onevet_quiz_progress'); } catch (_) {}
    refreshConcernUI();
    showScreen('intro');
  });

  /* ---------------- Keyboard Navigation ---------------- */

  function handleKeyboard(e) {
    if (state.current !== 'flow') return;
    // Don't intercept inside input/textarea
    if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;

    const currentStep = state.flow[state.stepIndex];

    // Arrow Left / Backspace → geri
    if (e.key === 'ArrowLeft' || (e.key === 'Backspace' && !e.target.matches('input,textarea'))) {
      if (state.stepIndex > 0) { e.preventDefault(); prevStep(); }
      return;
    }

    // Enter → concerns step'inde devam (multi-select)
    if (e.key === 'Enter') {
      if (currentStep === 'concerns' && state.answers.concerns.length > 0) {
        e.preventDefault();
        nextStep();
        return;
      }
    }

    // Number keys 1-9 → ilgili seçeneği seç
    if (/^[1-9]$/.test(e.key)) {
      const idx = parseInt(e.key, 10) - 1;
      const activeStep = root.querySelector(`[data-pq-step="${currentStep}"]`);
      if (!activeStep) return;
      const options = activeStep.querySelectorAll('.pq-option:not([style*="display: none"])');
      if (options[idx]) {
        e.preventDefault();
        options[idx].click();
      }
    }
  }

  document.addEventListener('keydown', handleKeyboard);

  /* ---------------- Inline Validation ---------------- */

  function validateField(name, value) {
    if (name === 'name') {
      const trimmed = (value || '').trim();
      if (trimmed.length < 2) return 'En az 2 karakter girmelisin';
      if (!/^[\p{L} '-]+$/u.test(trimmed)) return 'Adında sadece harf kullan';
      return null;
    }
    if (name === 'email') {
      const v = (value || '').trim();
      if (!v) return 'Email gerekli';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return 'Geçerli bir email gir';
      return null;
    }
    return null;
  }

  function showFieldError(fieldName, errorMsg) {
    const field = root.querySelector(`[data-pq-field="${fieldName}"]`);
    if (!field) return;
    const errEl = field.querySelector('[data-pq-error]');
    field.classList.toggle('has-error', !!errorMsg);
    if (errEl) errEl.textContent = errorMsg || '';
  }

  // Live validation on blur (touch only when user moves on)
  root.querySelectorAll('[data-pq-field] input').forEach((inp) => {
    inp.addEventListener('blur', () => {
      const fname = inp.closest('[data-pq-field]')?.dataset.pqField;
      if (!fname || fname === 'petName') return;
      const err = validateField(fname, inp.value);
      showFieldError(fname, err);
    });
    inp.addEventListener('input', () => {
      const fname = inp.closest('[data-pq-field]')?.dataset.pqField;
      if (!fname) return;
      const field = inp.closest('[data-pq-field]');
      if (field?.classList.contains('has-error')) {
        const err = validateField(fname, inp.value);
        showFieldError(fname, err);
      }
    });
  });

  /* ---------------- Resume from localStorage ---------------- */

  function tryResume() {
    try {
      const inProgress = localStorage.getItem('onevet_quiz_progress');
      if (!inProgress) return false;
      const data = JSON.parse(inProgress);
      const ageHours = (Date.now() - data.ts) / (1000 * 60 * 60);
      if (ageHours > 24) {
        localStorage.removeItem('onevet_quiz_progress');
        return false;
      }
      // Don't auto-resume — show "Devam et" CTA in intro
      const intro = root.querySelector('[data-pq-screen="intro"]');
      if (intro && data.stepIndex > 0) {
        const resumeBar = document.createElement('div');
        resumeBar.className = 'pq-resume-bar';
        resumeBar.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>
          <span>Yarım kalan quiz'in var · ${data.stepIndex + 1}/${state.flow.length} adımdaydın</span>
          <button type="button" class="pq-resume-btn" data-pq-resume>Devam et</button>
          <button type="button" class="pq-resume-skip" data-pq-resume-skip aria-label="Sıfırla">×</button>
        `;
        intro.insertBefore(resumeBar, intro.firstChild);
        resumeBar.querySelector('[data-pq-resume]').addEventListener('click', () => {
          state.answers = { ...state.answers, ...data.answers };
          if (data.answers.petType === 'dog') state.flow = STEPS_DOG;
          filterConcernsForPetType();
          refreshConcernUI();
          refreshPetName();
          showScreen('flow');
          showStep(state.flow[Math.min(data.stepIndex, state.flow.length - 1)]);
        });
        resumeBar.querySelector('[data-pq-resume-skip]').addEventListener('click', () => {
          localStorage.removeItem('onevet_quiz_progress');
          resumeBar.remove();
        });
      }
      return true;
    } catch (_) { return false; }
  }

  function saveProgress() {
    try {
      localStorage.setItem('onevet_quiz_progress', JSON.stringify({
        answers: state.answers,
        stepIndex: state.stepIndex,
        ts: Date.now(),
      }));
    } catch (_) {}
  }

  // Auto-save progress after each answer
  const _origShowStep = showStep;
  showStep = function (stepName) {
    _origShowStep(stepName);
    if (stepName !== 'email') saveProgress();
  };

  // On successful submit, clear in-progress
  const _origSubmitEmail = submitEmail;
  submitEmail = async function (form) {
    const ok = await _origSubmitEmail(form);
    if (ok) {
      try { localStorage.removeItem('onevet_quiz_progress'); } catch (_) {}
    }
    return ok;
  };

  // Init
  refreshPetName();
  tryResume();
})();
